import _ from 'lodash';
import api from '@/api/managerApi';
import urlBuilder from '@/utils/urlBuilder';
import { ROUTES } from '@/enums';
import Specialist from './service/specialist';
import Schedule from './service/schedule';
import Plan from './service/plan';

export default {
    Specialist,
    Schedule,
    Plan,

    /**
     * @param {UUID} eventId
     * @returns {Promise<EventServiceResource[]>}
     */
    getEventServices({ eventId }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.INDEX, { eventId });
        return api.get(url)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    getEventService({ eventId, serviceId }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.SHOW, { eventId, serviceId });
        return api.get(url)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    getEventLocations({ eventId }) {
        const url = urlBuilder(ROUTES.EVENT.API.LOCATIONS.INDEX, { eventId });
        return api.get(url)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    getAllEventLocations({ eventId }) {
        const url = urlBuilder(ROUTES.EVENT.API.LOCATIONS.ALL, { eventId });
        return api.get(url)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    /**
     * @param {UUID} eventId
     * @param {AddServiceToEventRequest} request
     * @return {Promise<EventServiceResource>}
     */
    addServiceToEvent({ eventId, request }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.STORE, { eventId });
        return api.post(url, request)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    exportFullSchedule({ eventId }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.EXPORT_FULL_SCHEDULE, { eventId });
        return api.get(url, {
            responseType: 'blob'
        })
            .then(response => Promise.resolve({
                blob: response.data,
                fileName: this._extractFileNameFromHeaders(_.get(response, 'headers', {}))
            }))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    _extractFileNameFromHeaders(headers) {
        const contentDisposition = headers['content-disposition'] || headers['Content-Disposition'];
        if (!contentDisposition) {
            return null;
        }

        const utfFileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utfFileNameMatch && utfFileNameMatch[1]) {
            return decodeURIComponent(utfFileNameMatch[1]);
        }

        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (fileNameMatch && fileNameMatch[1]) {
            return fileNameMatch[1];
        }

        return null;
    },

    importFullSchedule({ eventId, file }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.IMPORT_FULL_SCHEDULE, { eventId });
        const formData = new FormData();
        formData.append('file', file);

        return api.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    /**
     * @param {UUID} eventId
     * @param {UUID} eventServiceId
     * @param {UpdateEventServiceRequest} request
     * @return {Promise<EventServiceResource>}
     */
    updateEventService({ eventId, eventServiceId, request }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.UPDATE, { eventId, eventServiceId });
        return api.put(url, request)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    deleteEventService({ eventId, eventServiceId }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.DELETE, { eventId, eventServiceId });
        return api.delete(url)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    },

    clearEventServices({ eventId }) {
        const url = urlBuilder(ROUTES.EVENT.API.SERVICE.CLEAR_SERVICES, { eventId });
        return api.delete(url)
            .then(response => Promise.resolve(response.data.data))
            .catch(e => Promise.reject(_.get(e, 'response.data', e)));
    }
};
